import {ElementRef, Injectable, Input} from '@angular/core';
import Sockette from "sockette";
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import {AuthService} from '../api/auth.service';
import {ActivatedRoute} from '@angular/router';
import {ITestSessionDashboardInfo} from '../api/models/db/test-sessions.schema';
import {RoutesService} from '../api/routes.service';
import { TimezoneService } from '../core/timezone.service';
import { LangService } from '../core/lang.service';
import * as moment from 'moment-timezone';
import { AccountType } from '../constants/account-types';

interface SocketQueueItem {
  action: string,
  data: any
}

const HEARTBEAT_PING_INTERVAL  = 15 * 1e3;
const RECONNECT_TIMEOUT = 5 * 1e3;
const HEALTHCHECK_INTERVAL = 90 * 1e3;
const PING_TIMEOUT = 65 * 1e3;
const PING_TIMEOUT_MSG = 'ping-timeout';

enum WS_DISCONNECT_CODE {
  NORMAL_DISCONNECT = 1000,
  AWS_GONE_AWAY= 1001,
  DISCONNECT = 1005,
  ABNORMAL_DISCONNECT = 1006,
  PING_TIMEOUT = 3001
}

export interface IVideoCallRequest {
  type: 'VIDEO_CALL_REQUEST';
  fromUid: number;
  toUid: number;
  roomId: string;
  callLink: string;
  timestamp: number;
  sessionId?: number;
}


export interface IVideoCallResponse {
  type: "VIDEO_CALL_RESPONSE";
  fromUid: number;
  toUid: number;
  roomId: string;
  accepted: boolean;
  timestamp: number;
}

export enum VideoCallRequestType {
  VIDEO_CALL_REQUEST = 'VIDEO_CALL_REQUEST',
  VIDEO_CALL_RESPONSE = 'VIDEO_CALL_RESPONSE'
};

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  socket: any;
  private uri: string;
  public isSupport;
  public isSupervisor;
  public markingPoolId;
  public markingPoolGroupId;
  public uid;
  public currentMessage = '';
  communicationTimeout: NodeJS.Timeout;
  healthCheckInterval: NodeJS.Timeout;

  public networkOfflineSub: Subscription = null;
  private networkOnlineSub: Subscription = null;
  private offlineEvent: Observable<Event>;
  private onlineEvent: Observable<Event>;

  // Marker only.
  public supervisorId = null;
  public currentChat = [];

  // When marker is not in the chat component.
  public newMessage = false; //
  public messagePoolIds = [];
  pingInterval;
  public isSocketReady:BehaviorSubject<any> = new BehaviorSubject({});
  private socketReady: boolean = false;
  private socketQueue: Array<SocketQueueItem> = [];
  // Supervisor only
  // public chatTitle = "Current Chat";
  public markers = [];
  public broadcasts = [];
  public selectedMarker = null;
  public showBroadcasts = false;
  public messages = {};
  public isActive = {};
  public hasUnread = {};
  public names = {};
  public userType:string

  public isChatPanelVisible: boolean;

  public markingPool: any;

  // added for invigilator/test taker
  public isInvigilator = false;
  public isTestTaker = false;
  public group_id = null;
  public instit_group_id = null;
  private studentsForInvig = [];

  // for autoscroll
  public elem: ElementRef;

  private hasSuccessfullyOpened: boolean = false;

  constructor(private auth: AuthService,
              private route: ActivatedRoute,
              private routes: RoutesService,
              private timezone: TimezoneService,
              private lang: LangService) {
    // this.initSocket();
  }


  /**
   * Initializes the subscriptions for the network events
   * - If the network goes offline, the websocket is closed
   * - If the network comes back online, the websocket is reconnected
   * - Intializes the health check interval
   * @important should clear up the previous subscriptions before initializing new ones
   */
  private initSubscriptions() {
    if(this.networkOfflineSub) {
      this.networkOfflineSub.unsubscribe();
    }
    this.offlineEvent = fromEvent(window, 'offline');
    this.networkOfflineSub = this.offlineEvent.subscribe(e => {
      this.socket.close();
      this.socket = undefined;
      this.socketReady = false;
      this.clearWsIntervals();
      this.resetCommunicationTimeout();
    })

    if(this.networkOnlineSub) {
      this.networkOnlineSub.unsubscribe();
    }
    this.onlineEvent = fromEvent(window, 'online');
    this.networkOnlineSub = this.onlineEvent.subscribe(e => {
      this.initSocket();
      this.resetCommunicationTimeout();
    })

    this.auth.user().subscribe((info) => {
      this.onUserInfoChange(info);
    })

    this.setHealthCheckInterval();
  }

  ngOnDestroy(): void{
    console.log('ws', 'ngOnDestroy')
    clearInterval(this.healthCheckInterval); // should be here and not in clearWsIntervals()
    this.disconnect();
  }

  resetCommunicationTimeout(pingInterval = PING_TIMEOUT) {
    if(this.communicationTimeout) {
      clearTimeout(this.communicationTimeout);
    }
    this.communicationTimeout = setTimeout(this.didLoseCommunication, pingInterval);
  }

  didLoseCommunication = () => {
    // If we have not heard from the WS server in 35 seconds, disconnect.
    if (this.socket) {
      console.log('ws', 'student close ws')
      this.socket.close(WS_DISCONNECT_CODE.PING_TIMEOUT, PING_TIMEOUT_MSG);
      this.socket = undefined;
      this.clearWsIntervals();
    }

    this.socketReady = false;
    this.resetCommunicationTimeout();
  }

  scrollToBottom() {
    let self = this;
    setTimeout(function() {
      if (self.elem) {
        //console.log('scrolling to bottom');
        self.elem.nativeElement.scrollTop = self.elem.nativeElement.scrollHeight;
      } else {
        console.error('chatDiv is undefined');
      }
    }, 50);
  }

  setStudents(students) {
    this.studentsForInvig = students;
  }

  onUserInfoChange(info) {
    if(info && info.accountType) {
      //console.log('user info',info)
      this.userType = info.accountType;
      //console.log("userType",this.userType);
    }
  }

  async initSocket() {
    console.log('ws', 'connect()')
    this.initSubscriptions();

    this.setHealthCheckInterval();
    this.resetCommunicationTimeout();

    if(!this.uri){
      try {
        await this.getWebsocketUrl();
      } catch (err) {
        console.error('ws', err);
        return setTimeout(() => this.initSocket(), RECONNECT_TIMEOUT)
      }
    }
    this.socket = new Sockette(this.uri, {
      timeout: 10e3,
      maxAttempts: -1,
      onopen: e => {
        this.hasSuccessfullyOpened = true; 
        this.onSocketReady();
        if (this.isSupport) {
          this.onSupportConnect(e);
        } else if (this.isSupervisor) {
          this.onSupervisorConnect(e);
        } else if (!this.isInvigilator && !this.isTestTaker) {
          this.onMarkerConnect(e);
        } else if (this.isInvigilator) {
          this.onInvigConnect(e);
        } else if (this.isTestTaker) {
          this.onTestTakerConnect(e);
        }

      },
      onmessage: e => {
        this.resetCommunicationTimeout();
        if (this.isSupervisor || this.isInvigilator) {
          this.onSupervisorMessage(e);
        } else {
          this.onMarkerMessage(e);
        }
      },
      onreconnect: (ev:Event | CloseEvent) => {
        this.socketReady = false
        console.debug('ws', 'websocket trying to reconnect...', ev);
      },
      onmaximum: (ev: CloseEvent) => {
        console.debug('ws', 'websocket max connection attempts reached, giving up', ev);
        this.socketReady = false;
      },
      onclose: (ev: CloseEvent) => {
        this.logWebsocketInfo('WS_CLOSED', {
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          isTrusted: ev.isTrusted
        });

        this.socketReady = false
        this.onUserDisconnect(ev)
      },
      onerror: (ev: Event) => {
        // if the socket has not fully opened then we use WS_ERROR_CONNECT otherwise we use WS_ERROR slug
        const slug = this.hasSuccessfullyOpened ? 'WS_ERROR' : 'WS_ERROR_CONNECT';
        this.logWebsocketInfo(slug, {
          type: ev.type,
          timeStamp: ev.timeStamp,
          isTrusted: ev.isTrusted
        });
        console.log('ws', 'websocket error', ev);
        this.socketReady = false
      }
    });

    // Clear ping interval to not repeat ourselves
    if(this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    this.pingInterval = setInterval(() => {
      const data = {
        uid: this.uid.toString(),
        gameId: [this.markingPoolId.toString() + '-' + this.group_id],
        userType:this.userType
      }
      //console.log("sending ping data",data)
      this.emit('ping',data);
    }, HEARTBEAT_PING_INTERVAL);
  }

  private onUserDisconnect(ev: any) {
    const validDisconnectCodes = [WS_DISCONNECT_CODE.PING_TIMEOUT, WS_DISCONNECT_CODE.AWS_GONE_AWAY, WS_DISCONNECT_CODE.ABNORMAL_DISCONNECT, WS_DISCONNECT_CODE.DISCONNECT];
    if (validDisconnectCodes.includes(ev.code)) {
      setTimeout(() => this.initSocket(), RECONNECT_TIMEOUT); // throttle reconnect attempts
    }
  }

  /**
   * Clears all intervals and timeouts related to the service
   */
  private clearWsIntervals() {
    try {
      if(this.pingInterval) {
        clearInterval(this.pingInterval);
      }
      if(this.communicationTimeout) {
        clearTimeout(this.communicationTimeout);
      }
    } catch {}
  }

  public emit(action, data) {
    if(this.socketReady){
    this.socket.json({action, data});
    return;
    }
    this.socketQueue.push({ action, data });
  }

  private onSocketReady() {
    this.socketReady = true;
    while (this.socketQueue.length) {
      const { action, data } = this.socketQueue.shift();
      this.emit(action, data);
    }
  }

  onMarkerConnect(e) {
    //console.log('Connected as MRKR!');

    if (this.uid && this.markingPoolId) {
      const data = {
        uid: this.uid.toString(),
        gameId: [this.markingPoolId.toString()]
      };

      this.emit('playerConnect', data);
      this.removeUnread();
    } else {
      //console.log('external');
      // get all marking pools this player is in.
      this.auth.apiFind('public/mrkg-mrkr/marking-pools').then(pools => {
        const data = {
          uid: this.auth.user().value.uid.toString(),
          gameId: pools
        };

        this.emit('playerConnect', data);

      });
    }
  }

  private onMarkerMessage(e) {
    let eObj, eventType;
    try {
      eObj = JSON.parse(e.data);
      eventType = eObj.eventType;
    }
    catch (e) {
      return;
    }
    // console.log('Marker message received:', eventType);
    // console.log('Marker message received.');

    // Possible Events
    // getGame: returns all the game data this view needs.
    // message: a message has been received.
    // broadcast: a broadcast has been received.

    if (eventType === 'getGame') {
      const coachId = eObj.Attributes.coachId;
      const chat = eObj.Attributes.chat;

      this.currentChat = chat;
      this.supervisorId = coachId;

      // eObj.Attributes.playerData.unreadFrom.forEach(id => {
      //   if (id !== 'placeholder') {
      //     console.log('Pushed', id);
      //     this.messagePoolIds.push(id);
      //     this.newMessage = true;
      //   }
      // });
      this.scrollToBottom();

    } else if (eventType === 'message' || eventType === 'broadcast') {
      const msg = eObj.Attributes.message;

      this.playSound();

      // console.log(msg);
      this.newMessage = true;
      this.messagePoolIds.push(msg.poolId);

      this.currentChat.push(msg);

      if (!this.isTestTaker) {
        this.removeUnread();
      }
      this.scrollToBottom();

    }
    this.indexBroadcastMessages();
  }

  private onSupportConnect(e) {
    // console.log('Connected as support!');
    const data = {
      // uids
    };
    this.socket.json({action: 'supportConnect', data: data });
  }

  private onSupervisorConnect(e) {
    // console.log('Connected as SUPR!', e);

    // connectionId is available in the lambda.

    this.auth.apiFind('public/mrkg-supr/marking-pools', {
      query: {
        getAll: 1,
      }
    }).then(pools => {
      let currentPool = pools.data.find(pool => +pool.id === +this.markingPoolId);
      this.markingPoolGroupId = currentPool.group_id;

      // get all markers for the marking pool.
      this.auth.apiFind('public/mrkg-supr/marking-pool/markers', {
        query: {
          marking_pool_id: this.markingPoolId,
          only_users: true,
          marking_pool_group_id: this.markingPoolGroupId
        }
      }).then((markers: any) => {
        let uids = []; // markers.data.map(marker => marker.id.toString());

        markers.data.forEach(marker => {
          uids.push(marker.id.toString());
          this.names[marker.id] = marker.first_name + ' ' + marker.last_name;
        });

        const data = {
          gameId: this.markingPoolId,
          supervisorUid: this.uid,
          uids
        };

        this.socket.json({action: 'coachConnect', data: data });
      });
    });

  }

  private indexBroadcastMessages() {
    let index;
    const indexMessages = (messages) => {
      messages.forEach(msg => {
        if (msg.isBroadcast) {
          msg.broadcastIndex = index;
          ++index;
        }
      });
    }
    if (this.isTestTaker) {
      index = 1;
      indexMessages(this.currentChat);
      /*this.currentChat.forEach(msg => {
        if (msg.isBroadcast) {
          msg.broadcastIndex = index;
          ++index;
        }
      });*/
    } else {
      for (const uid in this.messages) {
        index = 1;
        indexMessages(this.messages[uid]);
        /*this.messages[uid].forEach(msg => {
          if (msg.isBroadcast) {
            msg.broadcastIndex = index;
            ++index;
          } 
        });*/
      }
    }
  }
  
  private onSupervisorMessage(e) {
    // console.log('Supervisor message received', e);
    // console.log('Supervisor message received.');

    const eObj = JSON.parse(e.data);
    const eventType = eObj.eventType;

    // Possible Events
    // gameData: Returns all the current game, which use to update the state
    // playerUpdate: playerList has changed or player has changed their activity.
    // message: message received from a player
    // playerChangeConnection: a player goes active or inactive.

    if (eventType === 'gameData') {
      const mPoolChat = eObj.Attributes;

      this.markers = mPoolChat.players;
      this.broadcasts = mPoolChat.broadcasts;
      this.messages = mPoolChat.messages;
      this.markingPool = mPoolChat;
      //console.log("**** checking is Active0*******")
      this.isActive = mPoolChat.isActive;
      this.hasUnread = mPoolChat.hasUnread;

      this.scrollToBottom();
    } else if (eventType === 'playerUpdate') {
      //console.log("****checking is Active1*******")
      const playerList = eObj.playerList;
      const currPlayer = eObj.currPlayer;
      const isActive = eObj.isActive;

      this.markers = playerList;
      //console.log("**** checking is Active2*******")
      this.isActive[currPlayer] = isActive;

    }  else if (eventType === 'message') {
      // console.log(eObj);

      const msg = eObj.Attributes.message;
      const markerId = eObj.Attributes.playerId;

      this.playSound();

      this.messages[markerId].push(msg);
      if (markerId == this.selectedMarker) {
        this.hasUnread[markerId] = false;

        // unset it as hasUnread=false on db.
        this.updateUnread(markerId, false);

      } else { // then we just update messages
        this.hasUnread[markerId] = true;
      }
      this.scrollToBottom();

    } else if (eventType === 'playerChangeConnection') {
       //console.log(eObj,"playerchangeconnectionObj");
      //console.log("**** checking is Active3*******")
      const isActive = eObj.Attributes.isActive;
      const playerId = eObj.Attributes.playerId;
      //console.log("**** checking is Active4*******")
      this.isActive[playerId] = isActive;

    }
    else if (eventType === 'updateDisconnectedTestTakers') {
     //console.log("**** disconeccting test takers chat*******",eObj)
      const disconnectedTestTakers = eObj.msg;
      if(disconnectedTestTakers && disconnectedTestTakers.length > 0){
      for(let testTakerUid of disconnectedTestTakers) {
        this.isActive[testTakerUid] = false;
      }
      }

   }
    this.indexBroadcastMessages();
  }

  private onInvigConnect(e) {
    // console.log('Connected as Invigilator!', e);

    if (this.studentsForInvig.length > 0) {
      let uids = [];
      this.studentsForInvig.forEach(student => {
        uids.push(student.id.toString());
        this.names[student.id] = student.name;
      });

      const data = {
        gameId: this.markingPoolId + '-' + this.group_id,
        supervisorUid: this.uid,
        uids
      };
      this.socket.json({action: 'coachConnect', data: data });
    } else {

      this.auth.apiFind('public/test-admin/test-sessions/students', {
        query: {
          test_session_group_id: this.group_id,
          instit_group_id: this.instit_group_id
        }
      }).then((students: any) => {
        let uids = []; // markers.data.map(marker => marker.id.toString());

        students.data.forEach(student => {
          uids.push(student.id.toString());
          this.names[student.id] = student.first_name + ' ' + student.last_name;
        });

        const data = {
          gameId: this.markingPoolId + '-' + this.group_id,
          supervisorUid: this.uid,
          uids
        };
        this.socket.json({action: 'coachConnect', data: data });
      }).catch(e => {
        console.warn(e);
      });

    }

  }

  //private onInvigMessage(e);
  // Don't need, same

  private onTestTakerConnect(e) {
    // console.log('Connected as Test Taker!');

    if (this.uid && this.markingPoolId) {
      const data = {
        uid: this.uid.toString(),
        gameId: [this.markingPoolId + '-' + this.group_id]
      };

      this.emit('playerConnect', data);
      this.removeUnread();
    }
  }

  // HELPERS

  // for supervisor
  updateUnread(playerId, unread) {
    const data = {
      'gameId': this.markingPoolId,
      'unread': unread,
      'playerId': playerId,
    };

    if (this.isInvigilator) {
      data.gameId = this.markingPoolId + '-' + this.group_id;
    }

    this.emit('updateUnread', data);
  }

  private playSound() {
    let audio = new Audio();
    audio.src = 'https://www.mathproficiencytest.ca/audio/when.mp3';
    audio.load();
    audio.play();
  }

  // for marker
  removeUnread() {
    const unreadData = {
      isAdd: false,
      uid: this.uid.toString(),
      markingPoolId: this.markingPoolId.toString()
    };

    if (this.isTestTaker) {
      unreadData.markingPoolId = this.markingPoolId + '-' + this.group_id;
    }

    // remove this one from unread list (locally).
    this.messagePoolIds.shift();
    // if that was the last one, disable the notification.
    if (this.messagePoolIds.length === 0) {
      this.newMessage = false;
    }
    this.emit('updatePlayerUnread', unreadData);
  }

  renderDate(d: Date) {
    var date = new Date(d);
    date.toString() // "Wed Jun 29 2011 09:52:48 GMT-0700 (PDT)"      
    const mStart = moment.tz(date, moment.tz.guess());
    const monthDate = mStart.format(this.lang.tra('datefmt_day_month_year'));
    const timeDate = mStart.format(this.lang.tra('timefmt_hour_time'));
    return`${monthDate} at ${timeDate}`;
    return this.timezone.moment(d).format(this.lang.tra('chat_datefmt_timestamp'));
  }

  sendMessage() {
    const message = this.currentMessage;
    // if no player has been selected, i.e. selectedMarker is null or undefined.
    if (!this.selectedMarker && !this.showBroadcasts) {
      console.log('returning...');
      return;
    }

    // if the message is empty
    if (!message && message !== '0') {
      console.log('empty message, returning');
      return;
    }

    const newMessage = {
      date: new Date(),
      isBroadcast: false,
      msg: message,
      sender: this.auth.getUid()
    };
    
    const gameId = this.markingPoolId + '-' + this.group_id;
    if (this.showBroadcasts) {
      newMessage.isBroadcast = true;

      // in this case, update broadcasts state too.
      this.broadcasts.push(newMessage);

      // update each marker chat in the client
      this.markers.forEach(marker => {
        const unreadData = {
          isAdd: true,
          uid: marker,
          markingPoolId: gameId
        };
        this.emit('updatePlayerUnread', unreadData);
        this.messages[marker].push(newMessage);
      });

      if (this.socket) {
        const data = {
          'message': message,
          'gameId': gameId,
          'coachId': this.auth.getUid()
        };

        this.emit('broadcast', data);
      }

    } else {
      const receiver = this.selectedMarker;
      if (this.socket) {
        const data = {
          'coachId': this.auth.getUid(),
          'message': message,
          'playerId': receiver,
          'gameId': gameId
        };

        const unreadData = {
          isAdd: true,
          uid: receiver,
          markingPoolId: gameId
        };

        this.emit('updatePlayerUnread', unreadData);
        this.emit('messageFromCoach', data);
      }


      this.messages[receiver].push(newMessage);

    }

    // this.chatService.currentChat.push(newMessage);
    this.currentMessage = '';
    this.scrollToBottom();
  }

  isConnected() {
    return this.socket;
  }

  disconnect() {
    //console.log("trying to  disconnect socket.....")
    if(this.socket) {
      this.socket.close();
      this.socketReady = false;
      //console.log("socket disconnected.....")
    }
  }

  public async logWebsocketInfo(slug, event) {
    console.log('ws', slug, event)

    return await this.auth.apiCreate(this.routes.LOG, {
      slug,
      data: {
        event,
        gameId: [this.markingPoolId.toString() + '-' + this.group_id],
        uid: this.auth.user().value.uid,
        userType: this.userType,
        wsURL: this.uri,
        connected: this.isConnected(),
      }
    })
  }

  public async setHealthCheckInterval() {
    if(this.healthCheckInterval) {
      return;
    }

    this.healthCheckInterval = setInterval(async ()=> {
      if(!this.isConnected()){
        await this.logWebsocketInfo('WS_HEALTHCHECK_FAIL', {});
        this.initSocket();
      }
    }, HEALTHCHECK_INTERVAL)
  }

  private async getWebsocketUrl() {
    return this.auth.apiFind(this.routes.TESTADMIN_WEBSOCKET_CONNECTED_USERS, {}).then((res) => {
      console.log('ws', 'getWebsocketUrl()', res.websocketUrl)
      this.uri = 'wss://' + res.websocketUrl;      
    })
  }
}
